import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Lock } from 'lucide-react';
import { authAPI } from '../services/api';

export default function ResetPassword() {
  const [searchParams] = useSearchParams();
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState('');
  const [error, setError] = useState('');
  const [token, setToken] = useState('');
  const navigate = useNavigate();

  useEffect(() => {
    const tokenFromUrl = searchParams.get('token');
    if (!tokenFromUrl) {
      setError('Invalid reset link. Please request a new password reset.');
    } else {
      setToken(tokenFromUrl);
    }
  }, [searchParams]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setMessage('');

    if (newPassword !== confirmPassword) {
      setError('Passwords do not match');
      return;
    }

    if (newPassword.length < 6) {
      setError('Password must be at least 6 characters');
      return;
    }

    setLoading(true);

    try {
      const response = await authAPI.resetPassword(token, newPassword);
      setMessage(response.message);
      setTimeout(() => {
        navigate('/');
      }, 2000);
    } catch (err) {
      setError(err.response?.data?.detail || 'Failed to reset password');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div style={{
      minHeight: '100dvh',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      background: 'linear-gradient(145deg, #f8fafc 0%, #f1f5f9 50%, #e2e8f0 100%)',
      padding: '24px 16px',
      boxSizing: 'border-box',
      position: 'relative',
      overflow: 'hidden'
    }}>
      {/* Soft Ambient Background Glow */}
      <div style={{
        position: 'absolute',
        top: '20%',
        left: '50%',
        transform: 'translateX(-50%)',
        width: '480px',
        height: '480px',
        background: 'radial-gradient(circle, rgba(5, 150, 105, 0.08) 0%, rgba(248, 250, 252, 0) 70%)',
        pointerEvents: 'none',
        zIndex: 0
      }} />

      <Card style={{
        width: '100%',
        maxWidth: '420px',
        backgroundColor: '#ffffff',
        boxShadow: '0 20px 40px -15px rgba(15, 23, 42, 0.10), 0 0 1px 1px rgba(15, 23, 42, 0.04)',
        border: '1px solid #e2e8f0',
        borderRadius: '16px',
        position: 'relative',
        zIndex: 1
      }}>
        <CardHeader className="space-y-1 text-center" style={{ padding: '24px 24px 12px' }}>
          <div className="mx-auto mb-2 flex items-center justify-center">
            <img 
              src="/assets/nutrimus-logo.png" 
              alt="NUTRIMUS" 
              style={{ height: '70px', width: 'auto', objectFit: 'contain' }}
            />
          </div>
          <CardTitle className="text-xl font-bold tracking-tight text-slate-900">Reset Password</CardTitle>
          <CardDescription className="text-xs text-slate-500">
            Masukkan password baru Anda untuk akun NUTRIMUS
          </CardDescription>
        </CardHeader>
        <CardContent style={{ padding: '8px 24px 24px' }}>
          <form onSubmit={handleSubmit} className="space-y-4">
            {message && (
              <div className="bg-emerald-50 border border-emerald-200 text-emerald-800 px-3.5 py-2.5 rounded-xl text-xs">
                {message}
                <p className="mt-1 text-xs text-emerald-600 font-semibold">Mengalihkan ke halaman login...</p>
              </div>
            )}
            
            {error && (
              <div className="bg-red-50 border border-red-200 text-red-800 px-3.5 py-2.5 rounded-xl text-xs">
                {error}
              </div>
            )}

            <div className="space-y-1.5">
              <Label htmlFor="newPassword" className="text-xs font-semibold text-slate-700">Password Baru</Label>
              <div className="relative">
                <Lock className="absolute left-3 top-3 h-4 w-4 text-slate-400" />
                <Input
                  id="newPassword"
                  type="password"
                  value={newPassword}
                  onChange={(e) => setNewPassword(e.target.value)}
                  placeholder="Minimal 6 karakter"
                  className="pl-9 h-10 text-xs bg-slate-50/80 border-slate-300 focus-visible:ring-emerald-500"
                  required
                  disabled={!token || !!message}
                />
              </div>
            </div>

            <div className="space-y-1.5">
              <Label htmlFor="confirmPassword" className="text-xs font-semibold text-slate-700">Konfirmasi Password</Label>
              <div className="relative">
                <Lock className="absolute left-3 top-3 h-4 w-4 text-slate-400" />
                <Input
                  id="confirmPassword"
                  type="password"
                  value={confirmPassword}
                  onChange={(e) => setConfirmPassword(e.target.value)}
                  placeholder="Ulangi password baru"
                  className="pl-9 h-10 text-xs bg-slate-50/80 border-slate-300 focus-visible:ring-emerald-500"
                  required
                  disabled={!token || !!message}
                />
              </div>
            </div>

            <Button 
              type="submit" 
              className="w-full bg-emerald-600 hover:bg-emerald-700 text-white font-semibold h-10 rounded-xl shadow-md shadow-emerald-600/25 transition-all text-xs" 
              disabled={loading || !token || !!message}
            >
              {loading ? 'Menyimpan password...' : 'Simpan Password Baru'}
            </Button>

            <Button
              type="button"
              variant="ghost"
              className="w-full text-xs text-slate-600 hover:text-slate-900 hover:bg-slate-100 rounded-xl h-9"
              onClick={() => navigate('/')}
            >
              Kembali ke Halaman Login
            </Button>
          </form>
        </CardContent>
      </Card>
    </div>
  );
}
